import { nombreDeMedioReserva } from '@/publico/dominio/formato';
/**
 * Flujo publico de reserva, de punta a punta.
 *
 * Cuatro pasos, sin registro, sin login y sin pagina comercial previa: al abrir la
 * aplicacion ya se ven el dia, las dos canchas y sus horarios.
 *
 *   1. `horario`     — dia, cancha, inicio y duracion.
 *   2. `datos`       — cedula, nombre, apellido, telefono y condiciones de cancelación.
 *   3. `resumen`     — todo lo que se esta por confirmar, y la accion explicita.
 *   4. `confirmada`  — comprobante amable con el detalle de la reserva.
 *
 * ## Quien decide que
 *
 * El servidor decide **precio, final, ventana, slots y disponibilidad** (docs/API.md).
 * Esta pantalla decide unicamente que mostrar y en que orden. Cuando la respuesta del
 * servidor contradice lo que la pantalla creia —el horario se ocupo, la duracion no se
 * ofrece, la cedula no es valida— gana el servidor y la interfaz se acomoda.
 *
 * ## Idempotencia y doble envio
 *
 * El boton de confirmar se deshabilita mientras la peticion esta en vuelo, asi que un
 * doble clic no manda dos peticiones. Y si mandara dos, o si hubiera que reintentar
 * porque no llego la respuesta, las dos llevarian la **misma** clave de idempotencia y
 * el servidor devolveria la misma reserva (ADR-024). El boton es comodidad; la clave es
 * la garantia.
 *
 * ## Datos personales
 *
 * Cedula, nombre, apellido y telefono viven en el estado de React y en ningun otro lado.
 * No van a la URL, ni a `localStorage`, ni a `sessionStorage`, ni a la consola. Al
 * confirmar se borran del estado y se descarta tambien `variables` de la mutacion, que
 * es la ultima copia que quedaria en memoria (docs/SEGURIDAD.md §6).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

import { VENTANA_PUBLICA_INICIAL, type CanchaId, type DuracionMinutos } from '@padel/domain';

import type {
  CanchaDisponibilidad,
  InicioDisponibilidad,
  MedioPagoReserva,
  ReservaCreada,
} from '@/infra/contratos';
import { Aviso } from '@/publico/componentes/Aviso';
import { Confirmacion } from '@/publico/componentes/Confirmacion';
import { EsqueletoGrilla } from '@/publico/componentes/EsqueletoGrilla';
import { FormularioCliente, type ErrorDeCampo } from '@/publico/componentes/FormularioCliente';
import { GrillaDeCancha } from '@/publico/componentes/GrillaDeCancha';
import { AvisoTransferenciaPendiente } from '@/publico/componentes/InformacionTransferencia';
import { PasosReserva, type PasoReserva } from '@/publico/componentes/PasosReserva';
import { PreciosPorHorario } from '@/publico/componentes/PreciosPorHorario';
import { ResumenReserva } from '@/publico/componentes/ResumenReserva';
import { SelectorDeDia } from '@/publico/componentes/SelectorDeDia';
import { SelectorDeDuracion } from '@/publico/componentes/SelectorDeDuracion';
import { datosDeCancha, nombreDeCancha } from '@/publico/dominio/canchas';
import type { FormularioReserva } from '@/publico/dominio/formulario';
import { diasVisibles, finDeTurno, hoyLocal } from '@/publico/dominio/horario';
import { useEstaEnLinea } from '@/publico/estado/conexion';
import { useCrearReserva, useDisponibilidad } from '@/publico/estado/consultas';
import { escribirSeleccion, leerSeleccion, turnoDe } from '@/publico/estado/seleccion';

interface PropiedadesReservaPublica {
  /** Instante de referencia. Se inyecta para que las pruebas no dependan del reloj. */
  readonly ahoraMs?: number;
}

/**
 * Lo que hace falta para pintar el comprobante.
 *
 * La respuesta de la callable no trae cancha ni duracion —no las necesita— asi que se
 * conservan del turno elegido. Es presentacion: los datos que mandan siguen siendo los
 * que devolvio el servidor.
 */
interface ReservaConfirmada {
  readonly respuesta: ReservaCreada;
  readonly canchaId: CanchaId;
  readonly duracionMinutos: DuracionMinutos;
  readonly medioPago: MedioPagoReserva;
}

function iniciosDe(cancha: CanchaDisponibilidad | undefined): readonly InicioDisponibilidad[] {
  return cancha?.dias[0]?.inicios ?? [];
}

export function ReservaPublica({ ahoraMs }: PropiedadesReservaPublica): React.JSX.Element {
  const [ahora] = useState(() => ahoraMs ?? Date.now());
  const [parametros, fijarParametros] = useSearchParams();
  const [paso, fijarPaso] = useState<PasoReserva>('horario');
  const [medioPago, fijarMedioPago] = useState<MedioPagoReserva | null>(null);
  const [datos, fijarDatos] = useState<FormularioReserva | null>(null);
  const [reserva, fijarReserva] = useState<ReservaConfirmada | null>(null);
  const tituloPaso = useRef<HTMLHeadingElement>(null);
  const bloqueDuracion = useRef<HTMLElement>(null);

  const hoy = hoyLocal(ahora);
  const dias = diasVisibles(ahora, VENTANA_PUBLICA_INICIAL.ventanaPublicaDias);
  const seleccion = leerSeleccion(parametros, hoy);
  const enLinea = useEstaEnLinea();

  const disponibilidad = useDisponibilidad(seleccion.fecha);
  const { mutacion, reiniciarIntento } = useCrearReserva(seleccion.fecha);
  const { error: errorReserva, isPending: enviando, mutate, reset: descartarEnvio } = mutacion;

  // Al llegar a la confirmacion se descarta el estado de la mutacion. Es lo que borra
  // `variables`, la ultima copia en memoria de los datos personales enviados.
  useEffect(() => {
    if (paso === 'confirmada') {
      descartarEnvio();
    }
  }, [paso, descartarEnvio]);

  // El foco viaja al titulo del paso nuevo: sin esto, quien navega con teclado o lector
  // de pantalla se queda al principio de la pagina y no se entera de que cambio.
  //
  // En el PRIMER montaje no se toca: robarle el foco a quien acaba de abrir la pagina lo
  // deja despues del titulo, y el primer tabulador ya no llega al enlace de salto.
  const pasoAnterior = useRef<PasoReserva | null>(null);
  useEffect(() => {
    if (pasoAnterior.current !== null && pasoAnterior.current !== paso) {
      tituloPaso.current?.focus();
    }
    pasoAnterior.current = paso;
  }, [paso]);

  /*
   * Al elegir un horario, el bloque de duracion aparece DEBAJO de las dos grillas. En una
   * pantalla de 360 px eso son varios miles de pixeles mas abajo: la persona toca un
   * horario y no pasa nada visible. Se lo trae a la vista.
   *
   * Sin animacion —`scrollIntoView` sin `behavior` es instantaneo— asi que no hay nada
   * que apagar con `prefers-reduced-motion`.
   */
  useEffect(() => {
    if (seleccion.localHora !== null) {
      bloqueDuracion.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [seleccion.canchaId, seleccion.localHora]);

  const cambiarSeleccion = useCallback(
    (cambios: {
      fecha?: string;
      canchaId?: CanchaId | null;
      localHora?: string | null;
      duracionMinutos?: DuracionMinutos | null;
    }): void => {
      const siguiente = { ...seleccion, ...cambios };
      fijarParametros(escribirSeleccion(siguiente), { replace: true });
      // Cambiar de turno es otra intencion: la clave de idempotencia anterior ya no
      // corresponde y hay que generar una nueva en el proximo envio (ADR-024).
      reiniciarIntento();
    },
    [seleccion, fijarParametros, reiniciarIntento],
  );

  const canchas = disponibilidad.data?.canchas ?? [];
  const canchaElegida = canchas.find((cancha) => cancha.canchaId === seleccion.canchaId);
  const inicioElegido = iniciosDe(canchaElegida).find(
    (inicio) => inicio.localHora === seleccion.localHora,
  );
  const opcionElegida = inicioElegido?.opciones.find(
    (opcion) => opcion.duracionMinutos === seleccion.duracionMinutos,
  );
  const turno = turnoDe(seleccion);
  const sinHorarios =
    disponibilidad.isSuccess &&
    canchas.every((cancha) =>
      iniciosDe(cancha).every((inicio) => !inicio.opciones.some((opcion) => opcion.disponible)),
    );

  const errorDeCampo: ErrorDeCampo | null =
    errorReserva?.campo === undefined
      ? null
      : { campo: errorReserva.campo, mensaje: errorReserva.message };

  const confirmar = useCallback((): void => {
    if (
      turno === null ||
      datos === null ||
      opcionElegida === undefined ||
      inicioElegido === undefined ||
      medioPago === null
    ) {
      return;
    }

    mutate(
      {
        canchaId: turno.canchaId,
        inicioUtc: inicioElegido.inicioUtc,
        duracionMinutos: turno.duracionMinutos,
        medioPago,
        cliente: datos.usarDatosRegistrados
          ? {
              cedula: datos.cedula,
              usarDatosRegistrados: true,
            }
          : {
              cedula: datos.cedula,
              nombre: datos.nombre,
              apellido: datos.apellido,
              telefono: datos.telefono,
              usarDatosRegistrados: false,
            },
        // En el canal público este campo acepta únicamente las condiciones de cancelación.
        consentimiento: datos.aceptaCancelacion,
      },
      {
        onSuccess: (creada: ReservaCreada) => {
          fijarReserva({
            respuesta: creada,
            canchaId: turno.canchaId,
            duracionMinutos: turno.duracionMinutos,
            medioPago,
          });
          // Se borran cedula, nombre, apellido y telefono en cuanto dejan de hacer falta.
          fijarDatos(null);
          reiniciarIntento();
          fijarPaso('confirmada');
        },
        // Cada fallo devuelve a la persona al paso donde puede arreglarlo. La
        // disponibilidad ya se invalido en `useCrearReserva`.
        onError: (fallo) => {
          if (fallo.conflicto) {
            fijarPaso('horario');
          } else if (fallo.campo !== undefined) {
            fijarPaso('datos');
          }
        },
      },
    );
  }, [turno, datos, opcionElegida, inicioElegido, medioPago, mutate, reiniciarIntento]);

  const empezarOtra = useCallback((): void => {
    fijarReserva(null);
    fijarDatos(null);
    fijarMedioPago(null);
    reiniciarIntento();
    cambiarSeleccion({ canchaId: null, localHora: null, duracionMinutos: null });
    fijarPaso('horario');
  }, [cambiarSeleccion, reiniciarIntento]);

  if (paso === 'confirmada' && reserva !== null) {
    return (
      <div className="reserva">
        <h1 className="titulo" ref={tituloPaso} tabIndex={-1}>
          Reserva confirmada
        </h1>
        <PasosReserva actual="confirmada" />
        <Confirmacion
          alEmpezarOtra={empezarOtra}
          canchaNombre={nombreDeCancha(reserva.canchaId)}
          duracionMinutos={reserva.duracionMinutos}
          medioPago={reserva.medioPago}
          reserva={reserva.respuesta}
        />
      </div>
    );
  }

  return (
    <div className="reserva">
      <div
        className={
          paso === 'horario' ? 'reserva__cabecera reserva__cabecera--horario' : 'reserva__cabecera'
        }
      >
        <div className="reserva__introduccion">
          <h1 className="titulo" ref={tituloPaso} tabIndex={-1}>
            {paso === 'horario'
              ? 'Reserva tu cancha'
              : paso === 'datos'
                ? 'Tus datos'
                : 'Confirma tu reserva'}
          </h1>

          <PasosReserva actual={paso} />

          {paso === 'horario' ? (
            <div className="bloque">
              <h2 className="bloque__titulo" id="titulo-horarios">
                Elegi dia y horario
              </h2>
              <SelectorDeDia
                alElegir={(fecha) => {
                  cambiarSeleccion({
                    fecha,
                    canchaId: null,
                    localHora: null,
                    duracionMinutos: null,
                  });
                }}
                dias={dias}
                fechaElegida={seleccion.fecha}
                hoy={hoy}
              />
            </div>
          ) : null}
        </div>
        {paso === 'horario' ? (
          <PreciosPorHorario
            cargando={disponibilidad.isPending}
            error={disponibilidad.isError}
            tarifas={disponibilidad.data?.tarifas ?? []}
          />
        ) : null}
      </div>

      {enLinea ? null : (
        <Aviso tono="aviso" titulo="Parece que estas sin conexion">
          <p>
            Los horarios que ves pueden estar desactualizados. Volve a intentar cuando recuperes
            internet.
          </p>
        </Aviso>
      )}

      {paso === 'horario' ? (
        <section aria-labelledby="titulo-horarios" className="bloque">
          {errorReserva?.conflicto === true ? (
            <Aviso tono="error" titulo="Ese horario ya no esta libre">
              <p>{errorReserva.message}</p>
            </Aviso>
          ) : null}

          {disponibilidad.isPending ? (
            <>
              <p role="status">Buscando horarios disponibles…</p>
              <EsqueletoGrilla />
            </>
          ) : null}

          {disponibilidad.isError ? (
            <Aviso
              accion={{
                etiqueta: 'Reintentar',
                alPulsar: () => {
                  void disponibilidad.refetch();
                },
                ocupado: disponibilidad.isFetching,
              }}
              tono="error"
              titulo="No pudimos cargar los horarios"
            >
              <p>{disponibilidad.error.message}</p>
            </Aviso>
          ) : null}

          {disponibilidad.isSuccess ? (
            <>
              {sinHorarios ? (
                <Aviso tono="info" titulo="No quedan horarios libres para este dia">
                  <p>Proba con otro dia de la lista.</p>
                </Aviso>
              ) : null}

              <p className="bloque__bajada">
                Elegí a qué hora empezar. Mostramos solo turnos disponibles, con su inicio y final.
                Podés empezar cuando termina otro turno, sin espera entre reservas.
              </p>
              <div className="canchas">
                {canchas.map((cancha) => {
                  const { nombre, descripcion } = datosDeCancha(cancha.canchaId);
                  return (
                    <GrillaDeCancha
                      alElegir={(canchaId, localHora) => {
                        cambiarSeleccion({ canchaId, localHora, duracionMinutos: null });
                      }}
                      canchaElegida={seleccion.canchaId}
                      canchaId={cancha.canchaId}
                      descripcion={descripcion}
                      estado={cancha.estado}
                      horaElegida={seleccion.localHora}
                      inicios={iniciosDe(cancha)}
                      key={cancha.canchaId}
                      nombre={nombre}
                    />
                  );
                })}
              </div>
            </>
          ) : null}

          {inicioElegido === undefined || seleccion.canchaId === null ? null : (
            <section
              aria-labelledby="titulo-duracion"
              className="bloque bloque--destacado"
              ref={bloqueDuracion}
            >
              <h2 className="bloque__titulo" id="titulo-duracion">
                {nombreDeCancha(seleccion.canchaId)} a las {inicioElegido.localHora}
              </h2>

              <SelectorDeDuracion
                alElegir={(duracionMinutos) => {
                  cambiarSeleccion({ duracionMinutos });
                }}
                duracionElegida={seleccion.duracionMinutos}
                localHora={inicioElegido.localHora}
                opciones={inicioElegido.opciones}
              />

              <fieldset className="medio-pago">
                <legend>Método de pago</legend>
                <p>Elegí cómo vas a pagar la reserva.</p>
                <div className="medio-pago__opciones">
                  {(['efectivo', 'transferencia', 'combinado'] as const).map((medio) => (
                    <label className="medio-pago__opcion" key={medio}>
                      <input
                        checked={medioPago === medio}
                        aria-describedby={
                          medio === 'combinado' ? 'pago-combinado-explicacion' : undefined
                        }
                        name="medioPago"
                        onChange={() => {
                          fijarMedioPago(medio);
                          reiniciarIntento();
                        }}
                        type="radio"
                        value={medio}
                      />
                      {nombreDeMedioReserva(medio)}
                    </label>
                  ))}
                </div>
                <p className="nota" id="pago-combinado-explicacion">
                  Pago combinado: dividan el total de la cancha entre varias personas, pagando una
                  parte en efectivo y otra por transferencia.
                </p>
              </fieldset>

              {medioPago === 'transferencia' || medioPago === 'combinado' ? (
                <AvisoTransferenciaPendiente />
              ) : null}

              <div className="acciones">
                <button
                  className="boton boton--primario"
                  disabled={opcionElegida?.disponible !== true || medioPago === null}
                  onClick={() => {
                    fijarPaso('datos');
                  }}
                  type="button"
                >
                  Continuar
                </button>
              </div>
            </section>
          )}
        </section>
      ) : null}

      {paso === 'datos' ? (
        <section aria-labelledby="titulo-datos" className="bloque">
          <h2 className="bloque__titulo" id="titulo-datos">
            Completa tus datos
          </h2>
          <p className="bloque__bajada">
            Los usamos para identificar la reserva y avisarte. No creamos ninguna cuenta.
          </p>

          {medioPago === 'transferencia' || medioPago === 'combinado' ? (
            <AvisoTransferenciaPendiente />
          ) : null}

          <FormularioCliente
            alContinuar={(valores) => {
              fijarDatos(valores);
              fijarPaso('resumen');
            }}
            alVolver={() => {
              fijarPaso('horario');
            }}
            errorServidor={errorDeCampo}
          />
        </section>
      ) : null}

      {paso === 'resumen' ? (
        <section aria-labelledby="titulo-resumen" className="bloque">
          <h2 className="bloque__titulo" id="titulo-resumen">
            Revisa antes de confirmar
          </h2>

          {turno === null ||
          opcionElegida === undefined ||
          inicioElegido === undefined ||
          medioPago === null ? (
            <Aviso
              accion={{
                etiqueta: 'Volver a los horarios',
                alPulsar: () => {
                  fijarPaso('horario');
                },
              }}
              tono="error"
              titulo="Se perdio el horario elegido"
            >
              <p>Elegi el horario otra vez para poder confirmar.</p>
            </Aviso>
          ) : (
            <>
              <ResumenReserva
                canchaNombre={nombreDeCancha(turno.canchaId)}
                duracionMinutos={turno.duracionMinutos}
                fin={finDeTurno(inicioElegido.inicioUtc, turno.duracionMinutos)}
                localFecha={turno.fecha}
                localHora={turno.localHora}
                medioPago={medioPago}
                precioCentesimos={opcionElegida.precioCentesimos}
              />

              {medioPago === 'transferencia' || medioPago === 'combinado' ? (
                <AvisoTransferenciaPendiente />
              ) : null}

              <Aviso tono="aviso" titulo="Atención antes de confirmar">
                <p>
                  Al confirmar, la reserva queda registrada automáticamente. Si luego solicitás la
                  cancelación, se cobrará un cargo de $250.
                </p>
                <p>Para cancelar, comunicate con el complejo.</p>
              </Aviso>

              {errorReserva !== null &&
              !errorReserva.conflicto &&
              errorReserva.campo === undefined ? (
                <Aviso
                  // Con exactOptionalPropertyTypes, omitir no es lo mismo que pasar
                  // undefined: el reintento existe o la propiedad no viaja.
                  {...(errorReserva.incierto
                    ? {
                        accion: {
                          etiqueta: 'Reintentar',
                          alPulsar: confirmar,
                          ocupado: enviando,
                        },
                      }
                    : {})}
                  tono="error"
                  titulo={
                    errorReserva.incierto
                      ? 'No sabemos si la reserva llego'
                      : 'No pudimos confirmar la reserva'
                  }
                >
                  <p>{errorReserva.message}</p>
                  {errorReserva.incierto ? (
                    <p>
                      Reintentar es seguro: si la reserva ya se habia creado, no se crea otra ni se
                      cobra dos veces.
                    </p>
                  ) : null}
                </Aviso>
              ) : null}

              <div className="acciones">
                <button
                  className="boton boton--secundario"
                  disabled={enviando}
                  onClick={() => {
                    fijarPaso('datos');
                  }}
                  type="button"
                >
                  Modificar datos
                </button>
                <button
                  className="boton boton--primario"
                  disabled={enviando}
                  onClick={confirmar}
                  type="button"
                >
                  {enviando ? 'Confirmando…' : 'Confirmar reserva'}
                </button>
              </div>

              <p aria-live="polite" className="solo-lectores" role="status">
                {enviando ? 'Confirmando la reserva. Esperá un momento.' : ''}
              </p>
            </>
          )}
        </section>
      ) : null}
    </div>
  );
}
